/**
 * Starea de sesiune a dashboard-ului: cine este utilizatorul, în ce cont lucrează acum,
 * ce permisiuni are și cum comută între conturi.
 *
 * Sursa de adevăr este SERVERUL: `GET /auth/session` întoarce user + conturi + permisiuni.
 * Clientul nu decide niciodată ce are voie utilizatorul — permisiunile de aici servesc doar la
 * ascunderea acțiunilor care oricum ar fi respinse server-side (403/404).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError, api, onSessionExpired, preferBearerTransport, readCookie, storeToken } from './api.js';

/**
 * Metadatele răspunsului de sesiune. `cookieTransport: 'unavailable'` înseamnă că serverul nu a
 * primit niciun cookie de-al nostru, deși browserul ar putea păstra cookie-uri — mediul în care
 * „m-am autentificat, dar sesiunea nu se păstrează". Vezi `preferBearerTransport` în `api.ts`.
 */
interface SessionMeta {
  csrfToken?: string | null;
  cookieTransport?: 'ok' | 'unavailable';
}

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  isPlatformAdmin: boolean;
  locale: string;
}

export interface Membership {
  id: string;
  name: string;
  slug: string;
  role: string;
  status: string;
}

export interface SessionResponse {
  authenticated: boolean;
  /** Prezent doar când clientul a cerut transportul prin header (context fără cookie-uri). */
  sessionToken?: string;
  user?: SessionUser;
  accounts?: Membership[];
  activeAccountId?: string | null;
  permissions?: string[];
  role?: string | null;
  features?: string[];
  unreadNotifications?: number;
}

export interface SessionState {
  status: 'loading' | 'authenticated' | 'anonymous';
  user: SessionUser | null;
  accounts: Membership[];
  activeAccountId: string | null;
  permissions: string[];
  role: string | null;
  features: string[];
  unreadNotifications: number;
  /** Reîncarcă sesiunea de la server și întoarce starea proaspătă (util pentru decizii de rutare). */
  refresh: () => Promise<SessionResponse | null>;
  /** Autentificare; întoarce sesiunea rezultată, ca apelantul să poată ruta în funcție de rol. */
  login: (email: string, password: string) => Promise<SessionResponse | null>;
  logout: () => Promise<void>;
  switchAccount: (accountId: string) => Promise<void>;
  /** Iese din contul în care administratorul a intrat pentru suport. */
  leaveAccount: () => Promise<void>;
  can: (permission: string) => boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionState['status']>('loading');
  const [user, setUser] = useState<SessionUser | null>(null);
  const [accounts, setAccounts] = useState<Membership[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [role, setRole] = useState<string | null>(null);
  const [features, setFeatures] = useState<string[]>([]);
  const [unread, setUnread] = useState(0);

  const apply = useCallback((data: SessionResponse) => {
    // Serverul întoarce tokenul doar când l-am cerut explicit (nu putem folosi cookie-uri).
    if (data.sessionToken) storeToken(data.sessionToken);
    // Serverul ne spune explicit că sesiunea nu este validă ⇒ orice token păstrat local este mort.
    // Fără curățare, clientul ar rămâne blocat pe transportul cu token, cu un token respins, iar
    // următoarea autentificare ar părea că nu are efect.
    if (data.authenticated === false) storeToken(null);
    setUser(data.user ?? null);
    setAccounts(data.accounts ?? []);
    setActiveAccountId(data.activeAccountId ?? null);
    setPermissions(data.permissions ?? []);
    setRole(data.role ?? null);
    setFeatures(data.features ?? []);
    setUnread(data.unreadNotifications ?? 0);
    setStatus(data.authenticated ? 'authenticated' : 'anonymous');
  }, []);

  /**
   * Citește starea sesiunii de la server (sursa de adevăr) și, cu ocazia asta, aflăm și dacă
   * mediul curent poate trimite cookie-uri. Dacă nu poate — dar browserul poate citi cookie-ul de
   * CSRF, deci cookie-uri există — trecem pe transportul cu token în antet înainte ca utilizatorul
   * să apese „Intră în cont". Așa autentificarea cere de la început tokenul de sesiune și nu mai
   * există nicio fereastră în care „login reușit" să însemne „rămân pe ecranul de login".
   */
  const refresh = useCallback(async () => {
    try {
      const raw = await api.getRaw<{ data: SessionResponse; meta?: SessionMeta }>('/auth/session', {
        declareCookies: true,
      });
      const meta = raw.meta;
      // Serverul compară ce cookie-uri vedem noi cu ce a primit el. Dacă vreunul nu a ajuns,
      // cookie-urile nu circulă în acest context: trecem pe token înainte de orice autentificare.
      if (meta?.cookieTransport === 'unavailable') preferBearerTransport();
      apply(raw.data);
      return raw.data;
    } catch {
      // Eșec de rețea (server oprit, conexiune întreruptă): NU declarăm sesiunea pierdută dacă
      // utilizatorul era deja autentificat. La prima încărcare rămâne „anonymous", ca înainte.
      setStatus((current) => (current === 'loading' ? 'anonymous' : current));
      return null;
    }
  }, [apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Sesiune expirată în timpul utilizării: revenim la ecranul de autentificare, fără dialoguri tehnice.
  useEffect(() => {
    onSessionExpired(() => {
      storeToken(null);
      setUser(null);
      setAccounts([]);
      setPermissions([]);
      setRole(null);
      setStatus('anonymous');
    });
  }, []);

  /**
   * Autentificare cu confirmarea sesiunii.
   *
   * Ordinea rezolvă o clasă întreagă de buguri reale, nu una teoretică: în medii care nu pot
   * păstra sau trimite cookie-uri (iframe încorporat pe alt domeniu, cookie-uri terță parte
   * blocate, `SameSite` strict, mod privat) `POST /auth/login` întoarce 200, dar cererea
   * următoare nu mai este autentificată. Fără tratament special, garda de rută readuce utilizatorul
   * pe ecranul de login, fără niciun mesaj — exact simptomul „își dă login, dar nu mă
   * redirecționează în dashboard".
   *
   * De aceea: încercăm transportul implicit → confirmăm sesiunea la server → dacă nu se confirmă,
   * reluăm autentificarea pe transportul cu token în antet (nu depinde de cookie-uri) → dacă nici
   * așa nu se confirmă, aruncăm o eroare explicită, cu mesaj pentru utilizator. Niciodată nu
   * raportăm succes fără sesiune confirmată.
   */
  const login = useCallback(
    async (email: string, password: string) => {
      const attempt = async (forceBearer: boolean): Promise<SessionResponse | null> => {
        let payload: { twoFactorRequired?: boolean } | null = null;
        try {
          payload = await api.post<{ twoFactorRequired?: boolean }>(
            '/auth/login',
            { email, password },
            forceBearer ? { forceBearer: true } : {},
          );
        } catch (err) {
          // 419: cookie-ul CSRF nu a ajuns la server ⇒ contextul nu poate folosi cookie-uri.
          // Nu deranjăm utilizatorul cu asta, trecem direct pe transportul cu token.
          if (!forceBearer && err instanceof ApiError && err.code === 'csrf-failed') return null;
          throw err;
        }
        if (payload?.twoFactorRequired) {
          // 2FA este pregătit în platformă (schemă, criptare, coloane), dar codul nu poate fi cerut
          // încă din interfață. Spunem clar ce se întâmplă în loc să lăsăm o buclă mută.
          throw new ApiError({
            type: 'https://docs.menuvia.ro/errors/two-factor-required',
            title: 'Autentificare în doi pași',
            status: 403,
            detail:
              'Contul are autentificarea în doi pași activată. Cere administratorului platformei să o dezactiveze sau folosește un cont fără 2FA.',
          });
        }
        // `refresh` citește starea din server, deci decizia de rutare (restaurant vs. consolă)
        // se ia pe date reale, nu pe o presupunere din client.
        return refresh();
      };

      const first = await attempt(false);
      if (first?.authenticated) return first;

      // Sesiunea nu s-a putut confirma pe transportul cu cookie-uri: reluăm autentificarea pe
      // transportul cu token în antet, pe care mediul îl poate folosi indiferent de cookie-uri.
      preferBearerTransport();
      const second = await attempt(true);
      if (second?.authenticated) return second;

      throw new ApiError({
        type: 'https://docs.menuvia.ro/errors/session-not-established',
        title: 'Sesiunea nu s-a putut păstra',
        status: 409,
        detail:
          'Datele de acces sunt corecte, dar acest cadru nu permite păstrarea sesiunii. Deschide aplicația într-o filă proprie și autentificarea va funcționa.',
      });
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      storeToken(null);
      setUser(null);
      setAccounts([]);
      setPermissions([]);
      setRole(null);
      setFeatures([]);
      setActiveAccountId(null);
      setStatus('anonymous');
    }
  }, []);

  const switchAccount = useCallback(
    async (accountId: string) => {
      await api.post('/auth/switch-account', { accountId });
      await refresh();
    },
    [refresh],
  );

  const leaveAccount = useCallback(async () => {
    await api.post('/auth/leave-account', {});
    await refresh();
  }, [refresh]);

  const can = useCallback(
    (permission: string) => {
      // Într-un cont de restaurant, administratorul vede toate acțiunile (adăugare, editare, ștergere),
      // nu doar pe cele ale unui rol de staff.
      if (user?.isPlatformAdmin && activeAccountId) return true;
      return permissions.includes(permission);
    },
    [permissions, user?.isPlatformAdmin, activeAccountId],
  );

  const value = useMemo<SessionState>(
    () => ({
      status,
      user,
      accounts,
      activeAccountId,
      permissions,
      role,
      features,
      unreadNotifications: unread,
      refresh,
      login,
      logout,
      switchAccount,
      leaveAccount,
      can,
    }),
    [status, user, accounts, activeAccountId, permissions, role, features, unread, refresh, login, logout, switchAccount, leaveAccount, can],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession trebuie folosit în interiorul <SessionProvider>');
  return ctx;
}

/** Helper pentru mesaje de eroare sigure la afișare (fără detalii tehnice — regula #37). */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  if (error instanceof TypeError || (error instanceof Error && /failed to fetch|network/i.test(error.message))) {
    return 'Nu am putut contacta serverul. Verifică conexiunea la internet și reîncearcă.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Ceva nu a funcționat. Reîncearcă.';
}

/** Erorile pe câmpuri, pentru formulare (422). */
export function fieldErrors(error: unknown): Record<string, string[]> {
  return error instanceof ApiError ? error.errors : {};
}
